/**
 * Selección de material para el Día 4.
 *
 * Función PURA: recibe el banco y devuelve qué elementos tocan hoy. Ningún
 * componente decide la política.
 *
 * REGLA DURA: los `dominado` y los `archivado` NO entran. El banco tiene que
 * concentrarse en lo que todavía aporta, y lo dominado sale de la vista activa
 * sin borrarse (queda en el historial).
 */

import {
  estaActivo,
  type ElementoBanco,
  type EstadoElemento,
} from '../modelo/banco.js';
import { ORDEN_PRIORIDAD } from '../modelo/prioridad.js';

/**
 * Orden de selección, de más a menos urgente:
 *
 *   1. prioridad (Alta primero)  — es la única señal MANUAL, y manda
 *   2. vecesOcurrido (más alto)  — lo que se repite duele más
 *   3. ultimaPracticaEn (más antiguo, o nunca) — recuperar tras un intervalo
 *   4. creadoEn                  — desempate estable, para que el orden no baile
 *
 * No es un algoritmo sofisticado a propósito: es una lista de prioridades
 * legible, que se puede explicar en una frase y no se comporta como una caja
 * negra.
 */
export function compararParaRetrieval(a: ElementoBanco, b: ElementoBanco): number {
  const porPrioridad = ORDEN_PRIORIDAD[a.prioridad] - ORDEN_PRIORIDAD[b.prioridad];
  if (porPrioridad !== 0) return porPrioridad;

  const porOcurrencias = b.vecesOcurrido - a.vecesOcurrido;
  if (porOcurrencias !== 0) return porOcurrencias;

  // Nunca practicado va primero: es lo que más falta hace recuperar.
  const practicaA = a.ultimaPracticaEn ?? '';
  const practicaB = b.ultimaPracticaEn ?? '';
  if (practicaA !== practicaB) return practicaA < practicaB ? -1 : 1;

  return a.creadoEn < b.creadoEn ? -1 : a.creadoEn > b.creadoEn ? 1 : 0;
}

/** Los que pueden entrar hoy: activos o aprendiendo, y vivos. */
export function elegiblesParaRetrieval(banco: ElementoBanco[]): ElementoBanco[] {
  return banco.filter(estaActivo);
}

/**
 * Material de una sesión de retrieval.
 *
 * `maximo` sale del curriculum, no de la interfaz: cada duración pide una
 * cantidad distinta.
 */
export function seleccionarParaRetrieval(
  banco: ElementoBanco[],
  maximo: number,
): ElementoBanco[] {
  return [...elegiblesParaRetrieval(banco)]
    .sort(compararParaRetrieval)
    .slice(0, Math.max(0, maximo));
}

/**
 * Resuelve la instantánea guardada en la sesión.
 *
 * Si el usuario edita el banco a mitad de sesión, el material de ESA sesión no
 * cambia: se resuelve por id contra lo que hay. Un elemento borrado mientras
 * tanto simplemente desaparece de la lista, sin romper nada.
 */
export function materialDeSesion(
  banco: ElementoBanco[],
  ids: string[],
): ElementoBanco[] {
  const porId = new Map(banco.map((e) => [e.id, e]));
  return ids.map((id) => porId.get(id)).filter((e): e is ElementoBanco => e !== undefined);
}

// ── Resultado de la práctica ───────────────────────────────────────────────

export const RESULTADOS_PRACTICA = ['facil', 'costo', 'no_salio'] as const;
export type ResultadoPractica = (typeof RESULTADOS_PRACTICA)[number];

export const NOMBRE_RESULTADO: Record<ResultadoPractica, string> = {
  facil: 'Me salió fácil',
  costo: 'Me costó',
  no_salio: 'No me salió',
};

/**
 * Estado que corresponde a un elemento tras practicarlo.
 *
 * NUNCA marca `dominado`: que algo salga una vez no significa que esté
 * dominado, y automatizarlo vaciaría el banco de cosas que todavía fallan bajo
 * presión. Dominar es una decisión MANUAL.
 *
 * Lo único que hace automáticamente es mover `activo` → `aprendiendo` cuando
 * costó o no salió: refleja que ya se está trabajando, sin sacarlo de la
 * práctica.
 */
export function estadoTrasPracticar(
  estadoActual: EstadoElemento,
  resultado: ResultadoPractica,
): EstadoElemento {
  if (estadoActual !== 'activo') return estadoActual;
  return resultado === 'facil' ? 'activo' : 'aprendiendo';
}
